import { filter, fx, pipe, toArray, uniqBy } from '@fxts/core';
import { MpsCategory } from './accessor.ts';
import { CategoryFilterLevel, CategoryIndex } from './types';

// 카테고리 계층 구조를 평면화
export const flattenCategories = (mpsCategories: MpsCategory[]): MpsCategory[] => {
  return fx(mpsCategories)
    .flatMap((cat) => [cat, ...cat.getChildren(), ...cat.getChildren().flatMap((c) => c.getChildren())])
    .toArray();
};

// 특정 레벨의 카테고리만 추출
export const findCategoriesByLevel = (mpsCategories: MpsCategory[], level: number): MpsCategory[] => {
  return fx(flattenCategories(mpsCategories))
    .filter((cat) => cat.getLevel() === level)
    .toArray();
};

// 카테고리 인덱스 구축
export const buildCategoryIndex = (mpsCategories: readonly MpsCategory[]): Map<string, CategoryIndex> => {
  const index = new Map<string, CategoryIndex>();

  mpsCategories.forEach((l1) => {
    const level2Codes = new Set(
      l1
        .getChildren()
        .map((l2) => l2.getCode())
        .filter((code): code is string => code !== undefined),
    );

    const level3Codes = new Set(
      l1
        .getChildren()
        .flatMap((l2) => l2.getChildren())
        .map((l3) => l3.getCode())
        .filter((code): code is string => code !== undefined),
    );

    index.set(l1.getCode(), { level2: level2Codes, level3: level3Codes });
  });

  return index;
};

// 레벨별 필터링 (모든 레벨 code로 통일)
export const filterByLevelAndParent = (
  index: Map<string, CategoryIndex>,
  root_code: string,
  level: CategoryFilterLevel,
  categories: MpsCategory[],
): MpsCategory[] => {
  const allowedCodes = index.get(root_code)?.[level];

  if (!allowedCodes) return [];

  return pipe(
    categories,
    filter((category) => allowedCodes.has(category.getCode())),
    uniqBy((category) => category.getCode()),
    toArray,
  );
};

// 부모 카테고리 찾기
export const findParentCategory = (
  mpsCategories: MpsCategory[],
  childCode: string,
): MpsCategory | undefined => {
  for (const l1 of mpsCategories) {
    if (l1.getChildren().some((l2) => l2.getCode() === childCode)) return l1;
    for (const l2 of l1.getChildren()) {
      if (l2.getChildren().some((l3) => l3.getCode() === childCode)) return l2;
    }
  }
  return undefined;
};

// 재귀적으로 모든 하위 카테고리를 평탄화하고 level 순으로 정렬
export const flattenCategoriesRecursively = (categories: MpsCategory[]): MpsCategory[] => {
  return fx(categories)
    .flatMap((cat) => [cat, ...flattenCategoriesRecursively(cat.getChildren())])
    .toArray()
    .sort((a, b) => a.getLevel() - b.getLevel());
};
